import { defineStore } from "pinia";
import { ref, computed } from "vue";
import useMyFetch from "@stores/useMyFetch";
import { deepCopy } from "@utils/toolBox";
import handleError from "@utils/composables/handleError";
import { captureEvent } from "@/lib/analytics";

export const useMCampaignQuotasStore = defineStore("mCampaignQuotas", () => {
  const campaignQuotas = ref([]);
  const initialCampaignQuotas = ref([]);

  const allModalities = computed(() => campaignQuotas.value.flatMap((group) => group.answer_quotas));

  const hasQuotasChanged = computed(
    () => JSON.stringify(initialCampaignQuotas.value) !== JSON.stringify(campaignQuotas.value),
  );

  // actions
  async function fetchMCampaignQuota(surveyToken) {
    try {
      const { response, data } = await useMyFetch(`/surveys/${surveyToken}/quotas`).json();
      if (response.value.ok) {
        campaignQuotas.value = data.value.quotas;
        initialCampaignQuotas.value = deepCopy(data.value.quotas);
      }
    } catch (err) {
      handleError(err);
    }
  }
  async function saveMCampaignQuota() {
    const saved = await Promise.all(
      allModalities.value.map(async (answerQuota) => {
        const { data, response } = await useMyFetch(`/answer_quotas/${answerQuota.id}`)
          .patch({ answer_quota: answerQuota })
          .json();
        Object.assign(answerQuota, data.value);
        return response.value.ok;
      }),
    );
    if (saved.every(Boolean)) captureEvent("m_campaign_quotas_saved", { quotas_count: saved.length });

    initialCampaignQuotas.value = deepCopy(campaignQuotas.value);
  }
  const findAnswerQuota = (id) => allModalities.value.find((aq) => aq.id === id);

  async function updateAnswerQuotaDetection(id, detection) {
    const { response, data } = await useMyFetch(`/answer_quotas/${id}`).patch({ answer_quota: detection }).json();
    if (!response.value.ok) {
      handleError(data);
      return false;
    }

    const initial = initialCampaignQuotas.value.flatMap((group) => group.answer_quotas).find((aq) => aq.id === id);
    [findAnswerQuota(id), initial].forEach((aq) => aq && Object.assign(aq, detection));
    return true;
  }

  const quotaOptions = (targetId) =>
    campaignQuotas.value
      .filter((group) => group.survey_target_id === targetId)
      .flatMap((group) =>
        group.answer_quotas.map((aq) => ({ label: `${group.question_name} - ${aq.answer_name}`, value: aq.id })),
      );

  return {
    hasQuotasChanged,

    //getters
    campaignQuotas,
    initialCampaignQuotas,
    allModalities,

    //actions
    fetchMCampaignQuota,
    saveMCampaignQuota,
    updateAnswerQuotaDetection,
    findAnswerQuota,
    quotaOptions,
  };
});
